import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Search, Trash2, BookOpen, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import PageLayout from "@/components/PageLayout";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BibleStudiesPage() {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    date: new Date().toISOString().split('T')[0],
    notes: ""
  });

  useEffect(() => {
    fetchStudies();
  }, []);

  const fetchStudies = async (search = "") => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/bible-studies`, { 
        params,
        withCredentials: true 
      });
      setStudies(response.data);
    } catch (error) {
      console.error("Failed to fetch bible studies", error);
      toast.error("Failed to load bible studies");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    fetchStudies(query);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bible-studies`, formData, { withCredentials: true });
      toast.success("Bible study recorded successfully");
      setDialogOpen(false);
      setFormData({
        name: "",
        date: new Date().toISOString().split('T')[0],
        notes: ""
      });
      fetchStudies();
    } catch (error) {
      console.error("Failed to create bible study", error);
      toast.error("Failed to record bible study");
    }
  };

  const deleteStudy = async (studyId) => {
    if (!window.confirm("Are you sure you want to delete this bible study?")) return;
    
    try {
      await axios.delete(`${API}/bible-studies/${studyId}`, { withCredentials: true });
      toast.success("Bible study deleted");
      fetchStudies();
    } catch (error) {
      console.error("Failed to delete bible study", error);
      toast.error("Failed to delete bible study");
    }
  };

  return (
    <PageLayout title="Bible Studies Conducted">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="search-studies-input"
            type="text"
            placeholder="Search bible studies..."
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-study-button" className="bg-emerald-700 hover:bg-emerald-800">
              <Plus className="w-4 h-4 mr-2" />
              Log Study
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Log Bible Study Session</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Participant Name</label>
                <Input
                  data-testid="study-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="Person or group name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Study Date</label>
                <Input
                  data-testid="study-date-input"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <Textarea
                  data-testid="study-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Topics covered, scriptures discussed, progress, next steps..."
                  rows={5}
                />
              </div>
              <Button data-testid="submit-study-button" type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                Log Study
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
        </div>
      ) : studies.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No bible studies recorded yet</p>
          <p className="text-sm text-gray-400 mt-1">Start logging your bible study sessions</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {studies.map(study => (
            <StudyCard 
              key={study.id}
              study={study}
              onDelete={() => deleteStudy(study.id)}
            />
          ))}
        </div>
      )}
    </PageLayout>
  );
}

function StudyCard({ study, onDelete }) {
  return (
    <div className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-100">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{study.name}</h3>
          <div className="flex items-center text-sm text-gray-500 mb-3">
            <Calendar className="w-4 h-4 mr-1" />
            {format(new Date(study.date), 'MMMM d, yyyy')}
          </div>
          {study.notes && (
            <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">{study.notes}</p>
          )}
        </div>
        <Button
          data-testid={`study-delete-${study.id}`}
          onClick={onDelete}
          variant="ghost"
          size="sm"
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
